/**
 * Shared Wishlist Page
 * Public view of a shared wishlist (accessed via share link)
 */

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { doc, updateDoc } from 'firebase/firestore'
import { updateProfile } from 'firebase/auth'
import { useAuth } from '@/contexts/AuthContext'
import { useItems } from '@/hooks/useItems'
import { Wishlist, WishlistItem } from '@/types'
import { ItemCard } from '@/components/items/ItemCard'
import { SEO } from '@/components/seo/SEO'
import { StructuredData } from '@/components/seo/StructuredData'
import { NamePromptModal } from '@/components/common/NamePromptModal'
import { getWishlist } from '@/services/wishlist.service'
import { reserveItem } from '@/services/reservation.service'
import { analyticsEvents } from '@/utils/analytics'
import { db } from '@/config/firebase'
import toast from 'react-hot-toast'

export const SharedWishlist = () => {
  const { wishlistId } = useParams<{ wishlistId: string }>()
  const { user } = useAuth()
  const [wishlist, setWishlist] = useState<Wishlist | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNamePrompt, setShowNamePrompt] = useState(false)
  const [itemToReserve, setItemToReserve] = useState<WishlistItem | null>(null)
  const [isReserving, setIsReserving] = useState(false)

  const { items, itemsByCategory } = useItems(wishlist?.id)

  useEffect(() => {
    if (!wishlistId) return

    const fetchWishlist = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getWishlist(wishlistId)

        if (!data) {
          setError('Wishlist not found or no longer shared')
        } else if (data.visibility !== 'shared') {
          setError('This wishlist is private')
        } else {
          setWishlist(data)

          // Track wishlist view (as non-owner viewing shared link)
          analyticsEvents.wishlistViewed(data.id, false)
        }
      } catch (err) {
        console.error('Error loading wishlist:', err)
        setError('Failed to load wishlist')
      } finally {
        setLoading(false)
      }
    }

    fetchWishlist()
  }, [wishlistId])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading wishlist...</p>
        </div>
      </div>
    )
  }

  if (error || !wishlist) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{error || 'Wishlist Not Found'}</h2>
          <p className="text-gray-600 mb-6">
            This wishlist may have been deleted or is no longer being shared.
          </p>
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            Go to Homepage
          </Link>
        </div>
      </div>
    )
  }

  const isOwner = user?.uid === wishlist.ownerId
  const categories = wishlist.categories || []
  const uncategorizedItems = itemsByCategory['uncategorized'] || []

  const handleReserve = async (item: WishlistItem) => {
    if (!user) {
      // This should never happen since auto-sign-in creates anonymous users
      toast.error('Unable to reserve. Please refresh the page.')
      return
    }

    if (isOwner) {
      toast.error('You cannot reserve items on your own wishlist')
      return
    }

    // Check if user has a display name
    if (!user.displayName || user.displayName.trim() === '') {
      // Prompt for name
      setItemToReserve(item)
      setShowNamePrompt(true)
      return
    }

    // Proceed with reservation
    await performReservation(item)
  }

  const handleNameSubmit = async (name: string) => {
    if (!user || !itemToReserve) return

    setIsReserving(true)
    try {
      // Update user profile with display name
      await updateProfile(user, { displayName: name })

      // Also update Firestore user document
      const userRef = doc(db, 'users', user.uid)
      await updateDoc(userRef, { displayName: name })

      toast.success(`Name saved: ${name}`)

      // Now perform the reservation
      await performReservation(itemToReserve)

      // Close modal and reset
      setShowNamePrompt(false)
      setItemToReserve(null)
    } catch (error: any) {
      console.error('Error updating name:', error)
      toast.error('Failed to save name. Please try again.')
    } finally {
      setIsReserving(false)
    }
  }

  const performReservation = async (item: WishlistItem) => {
    if (!user) return

    try {
      await reserveItem(wishlist.id, item.id)

      // Track item reservation
      analyticsEvents.itemReserved(wishlist.id, item.id)

      toast.success(`Reserved "${item.title}"! The owner will be notified.`)
    } catch (error: any) {
      console.error('Error reserving item:', error)
      toast.error(error.message || 'Failed to reserve item')
      throw error
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      <SEO
        title={wishlist.title}
        description={wishlist.description || `Check out ${wishlist.title} - ${items.length} items wishlist on Paiseh! Free gift reservation to prevent duplicates.`}
        keywords={`${wishlist.title}, wishlist, gift registry, ${wishlist.occasion}, Singapore, paiseh`}
        canonicalUrl={`https://paiseh.sg/w/${wishlist.id}`}
        ogType="article"
      />

      {/* Structured Data */}
      <StructuredData
        type="itemList"
        name={wishlist.title}
        description={wishlist.description || undefined}
        items={items.map(item => ({
          name: item.title,
          description: item.description || undefined,
          url: item.url || undefined,
        }))}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Wishlist Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {wishlist.emoji && <span className="text-4xl">{wishlist.emoji}</span>}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{wishlist.title}</h1>
                {wishlist.description && (
                  <p className="text-gray-600 mt-2">{wishlist.description}</p>
                )}
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                  <span className="capitalize">{wishlist.occasion}</span>
                  <span>•</span>
                  <span>{items.length} items</span>
                  {wishlist.reservedCount > 0 && (
                    <>
                      <span>•</span>
                      <span className="text-green-600">{wishlist.reservedCount} reserved</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isOwner && (
              <Link
                to={`/wishlists/${wishlist.id}`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Manage Wishlist
              </Link>
            )}
          </div>
        </div>

        {/* Items Section */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Items</h2>

        {items.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 py-12 px-6">
            <div className="text-center max-w-lg mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Items Added Yet</h3>
              <p className="text-gray-600 mb-6">
                This wishlist is still being set up. The owner hasn't added any items yet, but check back soon - they're probably working on it!
              </p>

              {/* What to expect */}
              <div className="bg-gray-50 rounded-lg p-5 text-left">
                <h4 className="font-medium text-gray-900 mb-3">What You'll Find Here</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>A list of items the person would love to receive as gifts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Links to purchase items directly from stores</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>The ability to reserve items so others know what's already taken</span>
                  </li>
                </ul>
              </div>

              {/* CTA to create own wishlist */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-3">Want to create your own wishlist?</p>
                <Link
                  to="/"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                >
                  Create a Free Wishlist
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Categorized Items */}
            {categories.map(category => {
              const categoryItems = itemsByCategory[category.id] || []
              if (categoryItems.length === 0) return null

              return (
                <div key={category.id}>
                  <div
                    className="flex items-center gap-2 mb-4"
                    style={{ borderLeftColor: category.color, borderLeftWidth: 4 }}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 ml-3">{category.name}</h3>
                    <span className="text-sm text-gray-500">({categoryItems.length})</span>
                  </div>
                  <div className="space-y-3">
                    {categoryItems.map(item => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        isOwner={isOwner}
                        onReserve={handleReserve}
                        showReserveButton={!isOwner && wishlist.allowReservations && !!user}
                      />
                    ))}
                  </div>
                </div>
              )
            })}

            {/* Uncategorized Items */}
            {uncategorizedItems.length > 0 && (
              <div>
                {categories.length > 0 && (
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Uncategorized ({uncategorizedItems.length})
                  </h3>
                )}
                <div className="space-y-3">
                  {uncategorizedItems.map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      isOwner={isOwner}
                      onReserve={handleReserve}
                      showReserveButton={!isOwner && wishlist.allowReservations && !!user}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Name Prompt Modal */}
      <NamePromptModal
        isOpen={showNamePrompt}
        onClose={() => {
          setShowNamePrompt(false)
          setItemToReserve(null)
        }}
        onConfirm={handleNameSubmit}
        itemTitle={itemToReserve?.title || ''}
        isLoading={isReserving}
      />
    </div>
  )
}
